import { hasProtocol } from 'ufo'
import { useSchemaOrg } from '#imports'
import { buildArticleAuthor } from './buildArticleAuthor'
import { buildArticleImage } from './buildArticleImage'
import { buildProductSchema } from './buildProductSchema'
import { buildPublisher } from './buildPublisher'
import { extractHowToSteps } from './extractHowToSteps'
import { extractStateFromUrl } from './extractStateFromUrl'
import { extractVideoObjects } from './extractVideoObjects'
import { htmlToPlainText } from './htmlToPlainText'
import { resolveSeoMetadata } from './resolveSeoMetadata'

const IN_LANGUAGE = 'en-US'

function originOf(url: string): string {
  try {
    return new URL(url).origin
  }
  catch {
    return ''
  }
}

interface FaqEntry { question: string, answer: string }

const H4_SPLIT_RE = /(<h4[^>]*>[\s\S]*?<\/h4>)/g
// IMPORTANT: H4_BLOCK_RE is used with .exec() inside a loop and MUST stay
// non-global. Adding the /g flag would make `lastIndex` sticky between exec()
// calls and cause iterations to skip valid <h4> blocks.
const H4_BLOCK_RE = /^<h4[^>]*>([\s\S]*?)<\/h4>$/

// Accordions are generic expandable UI (product specs, "read more", etc.) — only
// treat items as Q&A when the title ends with '?'. Same rule as EditorialBlock h4.
function extractAccordionEntries(block: any): FaqEntry[] {
  const entries: FaqEntry[] = []
  for (const item of (block?.accordionItems?.expandedValue ?? [])) {
    const q = (item?.title?.value ?? '').trim()
    const a = htmlToPlainText(item?.body?.value)
    if (q.endsWith('?') && a)
      entries.push({ question: q, answer: a })
  }
  return entries
}

// Authors write FAQ questions as <h4> headings ending in '?' inside an editorial
// block; following siblings (until the next <h4>) are treated as the answer.
function extractEditorialEntries(block: any): FaqEntry[] {
  const entries: FaqEntry[] = []
  const html: string = block?.body?.value ?? ''
  const parts = html.split(H4_SPLIT_RE)
  let currentQ: string | null = null
  let answerHtml = ''

  const flush = () => {
    if (!currentQ)
      return
    const a = htmlToPlainText(answerHtml)
    if (a)
      entries.push({ question: currentQ, answer: a })
  }

  for (const part of parts) {
    const h4Match = H4_BLOCK_RE.exec(part)
    if (h4Match) {
      flush()
      const text = htmlToPlainText(h4Match[1])
      currentQ = text.endsWith('?') ? text : null
      answerHtml = ''
    }
    else if (currentQ) {
      answerHtml += part
    }
  }
  flush()
  return entries
}

// Detects FAQ Q&A pairs from CMS content across AccordionBlocks and EditorialBlocks.
function extractFaqEntries(content: any): FaqEntry[] {
  const blocks = [
    ...(content?.bodyBlockArea?.expandedValue ?? []),
    ...(content?.pageBlockArea?.expandedValue ?? []),
  ]
  const entries: FaqEntry[] = []
  for (const block of blocks) {
    const contentTypes: string[] = block?.contentType ?? []
    if (contentTypes.includes('AccordionBlock'))
      entries.push(...extractAccordionEntries(block))
    if (contentTypes.includes('EditorialBlock'))
      entries.push(...extractEditorialEntries(block))
  }
  return entries
}

export default () => {
  const getSchemaTypeMap = (content: any, name: string, articleImage: any, publisher: any, schemaParts: any[], crumbUrl: (a: any) => string | undefined): any => {
    return {
      ArticleCategoryFeedPage: () => schemaParts.push({ '@type': 'CollectionPage', 'name': `${name} Articles`, 'inLanguage': IN_LANGUAGE }),
      ArticleFeedLandingPage: () => schemaParts.push({ '@type': 'CollectionPage', 'name': `${name} Articles`, 'inLanguage': IN_LANGUAGE }),
      ArticleFeedPage: () => schemaParts.push({ '@type': 'CollectionPage', 'name': `${name} Articles`, 'inLanguage': IN_LANGUAGE }),
      ArticlePage: () => {
        const author = buildArticleAuthor(content?.articleAuthor)
        schemaParts.push(defineArticle({
          headline: name,
          datePublished: content?.created,
          dateModified: content?.changed,
          inLanguage: IN_LANGUAGE,
          publisher,
          ...(author ? { author } : {}),
          ...(articleImage ? { image: articleImage } : {}),
        }))
      },
      ArticleTagFeedPage: () => schemaParts.push({ '@type': 'CollectionPage', 'name': `${name} Articles`, 'inLanguage': IN_LANGUAGE }),
      OneEightySearchPage: () => schemaParts.push({ '@type': 'SearchResultsPage', name, 'inLanguage': IN_LANGUAGE }),
      ProductListingPage: () => schemaParts.push({
        '@type': 'CollectionPage',
        'name': `${name} Products`,
        'inLanguage': IN_LANGUAGE,
      }),
      ProductPage: () => {
        const node = buildProductSchema(content, name)
        schemaParts.push(node.aggregateRating ? defineProduct(node) : node)
      },
      QuestionCategoryPage: () => schemaParts.push({ '@type': 'CollectionPage', name, 'inLanguage': IN_LANGUAGE }),
      QuestionPage: () => schemaParts.push(defineArticle({
        '@id': `${content?.contentLink?.url}#article`,
        'headline': name,
        'datePublished': content.created,
        'dateModified': content.changed,
        'inLanguage': IN_LANGUAGE,
        'author': { '@type': 'Organization', 'name': '180 Medical' },
        publisher,
        'isPartOf': {
          '@type': 'CollectionPage',
          'name': (content.breadcrumbLinks?.expandedValue[content.breadcrumbLinks?.expandedValue?.length - 1]?.name) ?? 'Help Center',
          'url': crumbUrl(content.breadcrumbLinks?.expandedValue?.[content.breadcrumbLinks?.expandedValue?.length - 1]) ?? '',
        },
      })),
      QuestionSearchPage: () => schemaParts.push({ '@type': 'SearchResultsPage', name, 'inLanguage': IN_LANGUAGE }),
      TestimonialPage: () => schemaParts.push(
        defineWebPage({ name: content?.testimonialName?.value || name, inLanguage: IN_LANGUAGE }),
      ),
      TestimonialPageFeed: () => schemaParts.push({ '@type': 'CollectionPage', name, 'inLanguage': IN_LANGUAGE }),
    }
  }

  const setupSchema = async (content: any, currentUrl?: string) => {
    const appSettingsStore = useAppSettingsStore()

    useSchemaOrg(null)

    useSchemaOrg(() => {
      if (!content) {
        return
      }

      // Single source of truth for title/description/image — same resolver as
      // useSeo so HTML head, OG meta, and JSON-LD stay aligned. content.pageTitle
      // remains a last-resort fallback for content types that only populate it.
      const seo = resolveSeoMetadata(content, currentUrl)
      const name = seo.title || content?.pageTitle?.value

      // Prefer the user-facing request URL (e.g. /product-details/ostomy/foo)
      // over content.contentLink.url. For content types like ProductPage the CMS
      // returns the *template* URL (e.g. /products/ostomy/product/) which is not
      // what we want surfaced in schema URLs or @ids.
      const pageUrl = currentUrl || content?.contentLink?.url

      const pageOrigin = originOf(pageUrl)
      const crumbUrl = (crumb: any): string | undefined => {
        const canonicalPath = crumb?.url ?? crumb?.contentLink?.url
        if (!canonicalPath)
          return undefined
        return hasProtocol(canonicalPath) ? canonicalPath : `${pageOrigin}${canonicalPath}`
      }

      const schemaParts = []

      schemaParts.push(
        defineWebPage({
          '@id': `${pageUrl}#webpage`,
          name,
          'description': seo.description,
          'url': pageUrl,
          'inLanguage': IN_LANGUAGE,
        }),
      )

      const parentCrumbs = content.breadcrumbLinks?.expandedValue?.map((a: any, i: number) =>
        defineListItem({
          name: a.name,
          item: crumbUrl(a),
          position: i + 1,
        })) ?? []

      schemaParts.push(defineBreadcrumb({
        '@id': `${pageUrl}#breadcrumb`,
        'itemListElement': [
          ...parentCrumbs,
          defineListItem({
            name,
            position: parentCrumbs.length + 1,
          }),
        ],
      }))

      const articleImage = buildArticleImage(content, seo.images[0], pageUrl)
      const publisher = buildPublisher(appSettingsStore.getAppSettings, pageOrigin)

      const contentType = content.contentType[content.contentType.length - 1]
      const schemaTypeMap = getSchemaTypeMap(content, name, articleImage, publisher, schemaParts, crumbUrl)

      schemaTypeMap[contentType]?.()

      // FAQPage: emit when the page contains 2+ Q&A entries from AccordionBlocks
      // or EditorialBlocks following the <h4>-ending-in-? convention.
      const faqEntries = extractFaqEntries(content)
      if (faqEntries.length >= 2) {
        schemaParts.push({
          '@id': `${pageUrl}#faq`,
          '@type': 'FAQPage',
          'inLanguage': IN_LANGUAGE,
          'mainEntity': faqEntries.map(({ question, answer }) => ({
            '@type': 'Question',
            'name': question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': answer,
            },
          })),
        })
      }

      // HowTo: emit when the page has 2+ HowToStepBlocks. The block content
      // type is scaffolded in the frontend ahead of the matching Optimizely
      // backend PR — this helper is dormant (returns []) until the CMS adds
      // the block. Google requires at least 2 steps for a valid HowTo rich
      // result. Targets /how-to-cath-* pages once content is migrated.
      const howToSteps = extractHowToSteps(content)
      if (howToSteps.length >= 2) {
        schemaParts.push({
          '@id': `${pageUrl}#howto`,
          '@type': 'HowTo',
          'name': name,
          'description': seo.description,
          'inLanguage': IN_LANGUAGE,
          'step': howToSteps.map((s, i) => ({
            '@type': 'HowToStep',
            'position': i + 1,
            'name': s.name,
            'text': s.text,
            ...(s.imageUrl
              ? {
                  image: {
                    '@type': 'ImageObject',
                    'url': s.imageUrl,
                    ...(s.imageAlt ? { caption: s.imageAlt } : {}),
                  },
                }
              : {}),
          })),
        })
      }

      // VideoObject: one per OneEightyVideoBlock with all required fields
      // (name, description, thumbnailUrl, uploadDate, embedUrl). Emitting a
      // partial VideoObject triggers Search Console errors, so the gate is
      // strict — see extractVideoObjects for details.
      for (const video of extractVideoObjects(content, IN_LANGUAGE))
        schemaParts.push(video)

      // Service with areaServed: emit for state landing pages
      // (e.g. /180-medical-alabama/). Improves placement for state-level
      // service queries ("catheter supplier alabama") by describing the
      // mail-order delivery service offered to that state. Does not target
      // local-pack (no physical address) — that requires LocalBusiness.
      const stateName = extractStateFromUrl(pageUrl)
      if (stateName) {
        schemaParts.push({
          '@id': `${pageUrl}#service`,
          '@type': 'Service',
          'name': `180 Medical — Catheter & Ostomy Supply Delivery (${stateName})`,
          'serviceType': 'Medical Supply Delivery',
          'provider': { '@id': 'https://www.180medical.com/#identity' },
          'areaServed': {
            '@type': 'State',
            'name': stateName,
          },
        })
      }

      return schemaParts
    })
  }

  return { setupSchema }
}
